import React, { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { blogs } from '../data/blogsData';
import { FAQitem } from '../utils/FAQitem';


const faqs = [
    {
        question: 'How often should I check car fluids?',
        answer: 'A quick check once a month and before long drives is usually enough for most cars.'
    },
    {
        question: 'Do I need tools to check fluids?',
        answer: 'Most fluid checks only require basic access under the hood. Engine oil usually needs the dipstick, but no special tools are necessary.'
    },
    {
        question: 'Can I mix different fluids?',
        answer: 'It is usually better to use the fluid type recommended for your car instead of mixing different kinds.'
    },
    {
        question: 'What is the most important fluid to check?',
        answer: 'Engine oil is one of the most important because it directly affects engine protection and long-term reliability.'
    },
    {
        question: 'Can CarFido help me figure out whether a fluid looks normal?',
        answer: 'Yes. CarFido can help you think through fluid condition, likely warning signs, and what is worth checking next.'
    }
];

const Blog10 = () => {
    const navigate = useNavigate();

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    const blogId = 10;
    const blogData = blogs.find(b => b.id === blogId);

    return (
        <div className="bg-white">
            <div className="max-w-[1180px] mx-auto px-5 py-5 text-left">
                <button
                    onClick={() => navigate('/blogs')}
                    className="inline-flex fixed top-30 bg-black p-1 rounded-full hover:scale-110 items-center gap-2 text-gray-500 hover:text-gray-900 transition-colors text-sm font-medium mb-5"
                >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <line x1="19" y1="12" x2="5" y2="12"></line>
                        <polyline points="12 19 5 12 12 5"></polyline>
                    </svg>
                </button>
            </div>

            <div className="max-w-[1180px] mx-auto px-5 pb-[70px] flex flex-col lg:flex-row items-start gap-10">
                <main className="flex-1 max-w-full lg:max-w-[760px] min-w-0">
                    <section className="pb-6 border-b border-gray-200 mb-7">
                        <h1 className="text-3xl md:text-4xl lg:text-[42px] leading-[1.15] mb-3.5 text-gray-900 font-bold tracking-tight">
                            How to Check Your Car Fluids at Home: A Simple DIY Maintenance Guide
                        </h1>

                        <p className="text-lg md:text-xl text-gray-600 mb-[18px]">
                            If your car starts acting off, fluids are often where the problem begins. Low oil, old coolant, or dirty brake fluid can quietly affect how the car runs long before anything feels obviously wrong.
                        </p>

                        <div className="text-sm text-gray-500">
                            By <strong className="text-gray-900">CarFido Editorial Team</strong>
                            &nbsp;|&nbsp; Updated March 2026 &nbsp;|&nbsp; 9 min read
                        </div>
                    </section>

                    <figure className="mb-8">
                        <img
                            src="/blogs/10_1.jpeg"
                            alt="Driver checking car fluids at home under the hood"
                            title="How to Check Your Car Fluids at Home"
                            className="w-full h-auto block border border-gray-300 rounded-xl"
                        />
                        <figcaption className="text-xs text-gray-400 mt-2">
                            Featured image: car_fluid_check_featured.png
                        </figcaption>
                    </figure>

                    <nav className="bg-gray-50 border border-gray-200 rounded-xl p-5 mb-8">
                        <div className="text-base font-bold text-gray-900 mb-2.5">In this article</div>
                        <ul className="m-0 pl-[18px] text-gray-700 list-disc space-y-1">
                            <li><a href="#quick-answer" className="text-gray-900 no-underline hover:underline">Quick answer</a></li>
                            <li><a href="#fluid-summary" className="text-gray-900 no-underline hover:underline">Fluid check summary</a></li>
                            <li><a href="#why-it-matters" className="text-gray-900 no-underline hover:underline">Why it matters</a></li>
                            <li><a href="#engine-oil" className="text-gray-900 no-underline hover:underline">Engine oil</a></li>
                            <li><a href="#coolant" className="text-gray-900 no-underline hover:underline">Coolant</a></li>
                            <li><a href="#brake-fluid" className="text-gray-900 no-underline hover:underline">Brake fluid</a></li>
                            <li><a href="#transmission-fluid" className="text-gray-900 no-underline hover:underline">Transmission fluid</a></li>
                            <li><a href="#washer-fluid" className="text-gray-900 no-underline hover:underline">Washer fluid</a></li>
                            <li><a href="#when-not-to-ignore" className="text-gray-900 no-underline hover:underline">When not to ignore</a></li>
                            <li><a href="#dash-section" className="text-gray-900 no-underline hover:underline">How CarFido helps</a></li>
                            <li><a href="#faq" className="text-gray-900 no-underline hover:underline">FAQ</a></li>
                        </ul>
                    </nav>

                    <section className="mb-10 overflow-hidden">
                        <p className="mb-[18px] text-gray-700">
                            A lot of people do not think about fluids until the car starts feeling rough, running hot, or showing a warning light. By then, what should have been a quick check can turn into a bigger repair.
                        </p>
                        <p className="mb-[18px] text-gray-700">
                            The good part is that most fluid checks are simple. You do not need to be a mechanic to catch low levels, dirty fluid, or obvious signs that something needs attention.
                        </p>
                        <p className="mb-0 text-gray-700">
                            This guide breaks down the main fluids worth checking at home and what they can tell you about how the car is doing.
                        </p>
                    </section>

                    <section id="quick-answer" className="mb-9 bg-gray-50 border-l-4 border-gray-900 rounded-lg p-[18px]">
                        <div className="text-sm font-bold text-gray-500 uppercase tracking-widest mb-2">Quick Answer</div>
                        <p className="m-0 text-gray-900 font-medium">
                            <strong>How do you check your car fluids at home?</strong><br />
                            Most cars let you check engine oil using a dipstick and inspect coolant, brake fluid, and washer fluid through visible reservoirs. You are mainly checking for correct levels, clean condition, and any signs of leaks or contamination.
                        </p>
                    </section>

                    <section id="fluid-summary" className="mb-10">
                        <h2 className="text-2xl md:text-3xl lg:text-[31px] leading-tight mb-4 text-gray-900 font-bold">Fluid Check Summary</h2>
                        <div className="overflow-x-auto border border-gray-200 rounded-xl">
                            <table className="w-full border-collapse bg-white text-sm">
                                <thead className="bg-gray-50 text-gray-900 font-bold">
                                    <tr>
                                        <th className="text-left py-3.5 px-4 border-b border-gray-200">Fluid</th>
                                        <th className="text-left py-3.5 px-4 border-b border-gray-200">Where to Check</th>
                                        <th className="text-left py-3.5 px-4 border-b border-gray-200">What Matters</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr className="hover:bg-gray-50 transition-colors">
                                        <td className="py-3.5 px-4 border-b border-gray-200 font-medium text-gray-900">Engine oil</td>
                                        <td className="py-3.5 px-4 border-b border-gray-200 text-gray-600">Dipstick</td>
                                        <td className="py-3.5 px-4 border-b border-gray-200 text-gray-600">Level and condition</td>
                                    </tr>
                                    <tr className="hover:bg-gray-50 transition-colors">
                                        <td className="py-3.5 px-4 border-b border-gray-200 font-medium text-gray-900">Coolant</td>
                                        <td className="py-3.5 px-4 border-b border-gray-200 text-gray-600">Reservoir tank</td>
                                        <td className="py-3.5 px-4 border-b border-gray-200 text-gray-600">Level and clarity</td>
                                    </tr>
                                    <tr className="hover:bg-gray-50 transition-colors">
                                        <td className="py-3.5 px-4 border-b border-gray-200 font-medium text-gray-900">Brake fluid</td>
                                        <td className="py-3.5 px-4 border-b border-gray-200 text-gray-600">Master cylinder reservoir</td>
                                        <td className="py-3.5 px-4 border-b border-gray-200 text-gray-600">Level and color</td>
                                    </tr>
                                    <tr className="hover:bg-gray-50 transition-colors">
                                        <td className="py-3.5 px-4 border-b border-gray-200 font-medium text-gray-900">Transmission fluid</td>
                                        <td className="py-3.5 px-4 border-b border-gray-200 text-gray-600">Dipstick if available</td>
                                        <td className="py-3.5 px-4 border-b border-gray-200 text-gray-600">Level and smell</td>
                                    </tr>
                                    <tr className="hover:bg-gray-50 transition-colors">
                                        <td className="py-3.5 px-4 font-medium text-gray-900 border-b border-gray-200">Washer fluid</td>
                                        <td className="py-3.5 px-4 text-gray-600 border-b border-gray-200">Reservoir</td>
                                        <td className="py-3.5 px-4 text-gray-600 border-b border-gray-200">Keep it topped up</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </section>

                    <section id="why-it-matters" className="mb-11">
                        <h2 className="text-2xl md:text-3xl lg:text-[31px] leading-tight mb-4 text-gray-900 font-bold">Why It Matters</h2>
                        <p className="mb-4 text-gray-700">
                            Fluids are what keep the engine, brakes, cooling system, and transmission working the way they should. When levels drop or the fluid condition gets worse, the car usually starts losing protection long before something fails completely.
                        </p>
                        <p className="mb-5 text-gray-700">
                            That is why simple checks matter more than people think. A quick look under the hood can help you catch low fluid, contamination, or leaks before they turn into something expensive.
                        </p>
                        <ul className="pl-6 m-0 space-y-2 text-gray-700 list-disc">
                            <li>Low oil can speed up engine wear</li>
                            <li>Low coolant can lead to overheating</li>
                            <li>Old brake fluid can affect braking feel and safety</li>
                            <li>Neglected transmission fluid can make shifting problems more likely</li>
                        </ul>
                    </section>

                    <section id="engine-oil" className="mb-11">
                        <h2 className="text-2xl md:text-3xl lg:text-[31px] leading-tight mb-4 text-gray-900 font-bold">1. Check Engine Oil</h2>

                        <figure className="mb-5">
                            <img
                                src="/blogs/10_2.jpeg"
                                alt="Checking engine oil with a dipstick under the hood"
                                title="Engine oil dipstick check"
                                className="w-full h-auto block border border-gray-300 rounded-xl"
                            />
                            <figcaption className="text-xs text-gray-400 mt-2">
                                Inline image: engine_oil_dipstick_check.png
                            </figcaption>
                        </figure>

                        <p className="mb-4 text-gray-700">
                            Engine oil is one of the most important things you can check. It keeps internal parts lubricated and helps prevent wear from friction and heat.
                        </p>
                        <p className="mb-4 text-gray-700">
                            To check it properly, park on level ground, turn the engine off, wait a few minutes, then pull the dipstick out. Wipe it clean, insert it again, and pull it back out to read the level.
                        </p>
                        <p className="mb-0 text-gray-700">
                            You want the level between the minimum and maximum marks. Light brown or amber oil is generally normal. Very dark oil usually means it is overdue, and a milky look can point to a bigger problem.
                        </p>
                    </section>

                    <section id="coolant" className="mb-11">
                        <h2 className="text-2xl md:text-3xl lg:text-[31px] leading-tight mb-4 text-gray-900 font-bold">2. Check Coolant</h2>

                        <figure className="mb-5">
                            <img
                                src="/blogs/10_3.jpeg"
                                alt="Checking coolant level in the reservoir tank"
                                title="Coolant reservoir check"
                                className="w-full h-auto block border border-gray-300 rounded-xl"
                            />
                            <figcaption className="text-xs text-gray-400 mt-2">
                                Inline image: coolant_reservoir_check.png
                            </figcaption>
                        </figure>

                        <p className="mb-4 text-gray-700">
                            Coolant keeps the engine from overheating and helps regulate temperature in different driving conditions.
                        </p>
                        <p className="mb-4 text-gray-700">
                            Most cars let you check coolant by looking at the reservoir tank. You are checking whether the fluid sits between the marked lines and whether it still looks clean.
                        </p>
                        <p className="mb-0 text-gray-700">
                            Do not open the cap when the engine is hot. If the level is low or the coolant looks contaminated, it is worth paying attention before the car starts running too warm.
                        </p>
                    </section>

                    <section id="brake-fluid" className="mb-11">
                        <h2 className="text-2xl md:text-3xl lg:text-[31px] leading-tight mb-4 text-gray-900 font-bold">3. Check Brake Fluid</h2>

                        <p className="mb-4 text-gray-700">
                            Brake fluid is directly tied to safety, so it is worth checking when you are already under the hood.
                        </p>
                        <p className="mb-4 text-gray-700">
                            The reservoir is usually transparent enough to let you see the level from the outside. You are mainly looking for fluid near the marked range and a color that is still fairly clear to light yellow.
                        </p>
                        <p className="mb-0 text-gray-700">
                            If the fluid is getting very dark or the level keeps dropping, that is not something to brush off. It deserves attention before braking performance starts feeling different.
                        </p>
                    </section>

                    <section id="transmission-fluid" className="mb-11">
                        <h2 className="text-2xl md:text-3xl lg:text-[31px] leading-tight mb-4 text-gray-900 font-bold">4. Check Transmission Fluid</h2>

                        <p className="mb-4 text-gray-700">
                            Transmission fluid helps keep gear shifts smooth and protects the transmission from extra wear.
                        </p>
                        <p className="mb-4 text-gray-700">
                            Some cars still have a transmission dipstick, while others do not and need a more involved check. If yours does have one, you are usually looking for the correct level, normal color, and whether it smells burnt.
                        </p>
                        <p className="mb-0 text-gray-700">
                            Red or pink fluid is generally a good sign. Dark fluid or a burnt smell can point to overdue service or added transmission stress.
                        </p>
                    </section>

                    <section id="washer-fluid" className="mb-11">
                        <h2 className="text-2xl md:text-3xl lg:text-[31px] leading-tight mb-4 text-gray-900 font-bold">5. Check Windshield Washer Fluid</h2>


                        <p className="mb-4 text-gray-700">
                            Washer fluid is simple, but it still matters because visibility problems can get annoying fast.
                        </p>
                        <p className="mb-4 text-gray-700">
                            This one is usually just a matter of opening the reservoir and topping it up when the level gets low.
                        </p>
                        <p className="mb-0 text-gray-700">
                            It does not affect how the car runs, but it absolutely affects how easy the car is to drive in dust, bugs, bad weather, or on long trips.
                        </p>
                    </section>

                    <section id="when-not-to-ignore" className="mb-11">
                        <h2 className="text-2xl md:text-3xl lg:text-[31px] leading-tight mb-4 text-gray-900 font-bold">When You Should Not Ignore What You See</h2>
                        <p className="mb-4 text-gray-700">
                            A simple fluid check can also show you early warning signs that deserve more than a quick top-up.
                        </p>
                        <ul className="pl-6 m-0 space-y-2 text-gray-700 list-disc">
                            <li>Fluid levels that keep dropping quickly</li>
                            <li>Leaks under the car</li>
                            <li>Burning smells</li>
                            <li>Dirty or contaminated fluid</li>
                            <li>Warning lights or changes in how the car feels</li>
                        </ul>
                    </section>

                    <section id="common-mistakes" className="mb-11">
                        <h2 className="text-2xl md:text-3xl lg:text-[31px] leading-tight mb-4 text-gray-900 font-bold">What Most Drivers Get Wrong</h2>
                        <ul className="pl-6 m-0 space-y-2 text-gray-700 list-disc">
                            <li>Waiting until something feels wrong before checking anything</li>
                            <li>Looking only at fluid level and ignoring condition</li>
                            <li>Assuming full fluid always means everything is fine</li>
                            <li>Forgetting that repeated top-ups can point to a leak or bigger issue</li>
                        </ul>
                    </section>

                    <section id="dash-section" className="mb-10 overflow-hidden">
                        <div className="bg-gray-50 border border-gray-200 rounded-xl p-6">
                            <h2 className="text-2xl leading-tight mb-3.5 text-gray-900 font-bold">How CarFido Helps</h2>

                            <p className="mb-3.5 text-gray-700">
                                Fluid checks are simple in theory, but a lot of people still are not sure what they are looking at or whether something actually needs attention.
                            </p>

                            <p className="mb-3.5 text-gray-700">
                                CarFido helps you make more sense of fluid condition, warning signs, and what is worth checking next so you are not left guessing under the hood.
                            </p>

                            <p className="mb-2.5 text-gray-900 font-bold uppercase tracking-wide text-sm">You can ask questions such as:</p>

                            <ul className="pl-6 m-0 list-disc space-y-2 text-gray-700">
                                <li>Does this oil color look overdue?</li>
                                <li>Is it normal for coolant to drop a little over time?</li>
                                <li>What should I do if my brake fluid looks dark?</li>
                            </ul>
                        </div>
                    </section>

                    <section id="faq" className="mb-11 border-t border-gray-200 pt-8">
                        <h2 className="text-2xl md:text-3xl lg:text-[31px] leading-tight mb-5 text-gray-900 font-bold">Frequently Asked Questions</h2>
                        <div className="space-y-1">
                            {faqs.map((faq) => (
                                <FAQitem key={faq.question} question={faq.question} answer={faq.answer} />
                            ))}
                        </div>
                    </section>

                    <section id="final-thoughts" className="mb-11">
                        <h2 className="text-2xl md:text-3xl lg:text-[31px] leading-tight mb-4 text-gray-900 font-bold">Final Thoughts</h2>
                        <p className="mb-4 text-gray-700">
                            Checking your car fluids at home is one of the easiest ways to stay ahead of bigger problems without doing anything complicated.
                        </p>
                        <p className="mb-4 text-gray-700">
                            It only takes a few minutes, but it can help you catch low levels, worn fluid, or early warning signs before the repair bill gets bigger.
                        </p>
                        <p className="mb-0 text-gray-700">
                            If you stay consistent with it, fluid-related issues are a lot easier to catch early instead of after the car starts complaining.
                        </p>
                    </section>
                </main>

                <aside className="w-full lg:w-[300px] lg:flex-[0_0_300px] lg:sticky lg:top-5 self-start space-y-5">
                    <div className="border border-gray-200 rounded-xl bg-white p-5 shadow-sm">
                        <div className="text-base font-bold text-gray-900 mb-3">Quick Navigation</div>
                        <ul className="list-none p-0 m-0 space-y-2.5">
                            <li><a href="#quick-answer" className="no-underline text-gray-600 hover:text-gray-900 text-sm">Quick answer</a></li>
                            <li><a href="#fluid-summary" className="no-underline text-gray-600 hover:text-gray-900 text-sm">Fluid check summary</a></li>
                            <li><a href="#why-it-matters" className="no-underline text-gray-600 hover:text-gray-900 text-sm">Why it matters</a></li>
                            <li><a href="#engine-oil" className="no-underline text-gray-600 hover:text-gray-900 text-sm">Engine oil</a></li>
                            <li><a href="#coolant" className="no-underline text-gray-600 hover:text-gray-900 text-sm">Coolant reservoir</a></li>
                            <li><a href="#brake-fluid" className="no-underline text-gray-600 hover:text-gray-900 text-sm">Brake safety</a></li>
                            <li><a href="#transmission-fluid" className="no-underline text-gray-600 hover:text-gray-900 text-sm">Transmission</a></li>
                            <li><a href="#faq" className="no-underline text-gray-600 hover:text-gray-900 text-sm">FAQ</a></li>
                        </ul>
                    </div>

                    <div className="border border-gray-200 rounded-xl bg-gray-50 p-5 shadow-sm">
                        <div className="text-base font-bold text-gray-900 mb-2.5">Related Reads</div>
                        {(() => {
                            const otherBlogs = blogs.filter(b => b.id !== blogId);
                            const startIndex = blogId % otherBlogs.length;
                            return [...otherBlogs.slice(startIndex), ...otherBlogs.slice(0, startIndex)].slice(0, 3);
                        })().map((relatedBlog) => (
                            <Link
                                key={relatedBlog.id}
                                to={relatedBlog.path}
                                className="block no-underline text-gray-900 mb-3 text-sm leading-snug font-medium hover:text-gray-600 transition-colors"
                            >
                                {relatedBlog.title}
                            </Link>
                        ))}
                    </div>
                </aside>
            </div>
        </div>
    );
};

export default Blog10;
